import { FC, useCallback, useState } from "react";

import { atLeastOneStudentNotInGroup } from "../groups/helpers";

import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { ORDER_BY_CREATED, useCustomOrdered } from "mds/hooks/use-ordered";
import { useAppDispatch, useAppSelector } from "store";
import { useCapabilities } from "store/capability-hooks";
import {
  pageSelectors,
  selectActivityStatus,
  selectPageGroupsWithUsersByPageId,
  selectStudentsWithSectionActivityInCurrentSection,
} from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { isGroupPage } from "utils/page-utils";
import { usePresentation } from "utils/presentation";

interface StudentsNotInGroupBannerProps {
  pageId: string;
}

export const StudentsNotInGroupBanner: FC<StudentsNotInGroupBannerProps> = ({ pageId }) => {
  const dispatch = useAppDispatch();
  const showGroupConfig = useCallback(
    () => dispatch(viewStateActions.setShowGroupConfig(true)),
    [dispatch],
  );
  const [showBanner, setShowBanner] = useState(true);
  const { canAuthorSection } = useCapabilities();
  const { hasProjectorView } = usePresentation();
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["interaction_mode"]);
  const userActivity = useAppSelector(selectActivityStatus);
  // TODO: pageGroups is only used by atLeastOneStudentNotInGroup (order doesn't matter).
  // Add a selector that returns that boolean so this banner doesn't subscribe to the group objects.
  const pageGroups = useCustomOrdered(
    useAppSelector((s) => selectPageGroupsWithUsersByPageId(s, pageId)),
    ORDER_BY_CREATED,
  );
  const studentUsers = useListSelector(selectStudentsWithSectionActivityInCurrentSection);

  const showStudentsNotInGroupBanner =
    !hasProjectorView &&
    canAuthorSection &&
    isGroupPage(page) &&
    atLeastOneStudentNotInGroup(pageGroups, studentUsers, userActivity);

  if (!showStudentsNotInGroupBanner) {
    return null;
  }

  return showBanner ? (
    <Banner
      actionButtonText={t("warning.review_groups")}
      className="group-card"
      kind="warning"
      onAction={showGroupConfig}
      onDismiss={() => setShowBanner(false)}
    >
      {t("warning.not_assigned_to_groups")}
    </Banner>
  ) : null;
};
